import { useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import TestScreen from './TestScreen.tsx';
import Loader from '@/components/Loader.tsx';
import useExamComponent from '@/hooks/exam/useExamComponent.ts';

const TestReviewPage = () => {
  const { state } = useLocation();
  const { examId, clientExamId, examComponentId, question } = useParams();

  return (
    <TestReview
      examId={Number(examId)}
      examComponentId={Number(examComponentId)}
      clientExamId={Number(clientExamId)}
      question={Number(question)}
      fromPath={state?.from ?? '/dashboard'}
      exitPath={state?.exitPath}
    />
  );
};

interface TestReviewProps {
  examId: number;
  clientExamId: number;
  examComponentId: number;
  question: number;
  fromPath: string;
  /** Handed back to the review summary page, so its close button still leads to where the review was opened. */
  exitPath?: string;
}

const TestReview = ({ examId, examComponentId, clientExamId, fromPath, exitPath, ...props }: TestReviewProps) => {
  const navigate = useNavigate();
  const { loading, ...examComponentProps } = useExamComponent(examComponentId, clientExamId);

  const onComplete = useCallback(async () => {
    navigate(fromPath, { state: { from: exitPath } });
  }, [exitPath, fromPath, navigate]);

  const onQuestionChange = useCallback((questionNumber: number) => {
    navigate(`/review/${examId}/${clientExamId}/${examComponentId}/${questionNumber}`, {
      state: { from: fromPath, exitPath },
    });
  }, [examComponentId, clientExamId, examId, fromPath, exitPath, navigate]);

  if (loading) return <Loader />;

  return (
    <TestScreen
      onCompleteExam={onComplete}
      onQuestionChange={onQuestionChange}
      isPreview
      // Exit leaves the review entirely: to wherever the summary page closes to, or straight back to the page
      // that opened this question.
      exitPath={exitPath ?? fromPath}
      {...examComponentProps}
      {...props}
    />
  );
};

export default TestReviewPage;
